/**
 * API Client with JWT Token Handling
 *
 * Axios-based HTTP client configured for the Food Tracker API.
 * Automatically includes JWT tokens in request headers and handles authentication errors.
 * Integrates with offline queue for automatic request retry when connection is restored.
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { addToQueue } from './offlineQueue';

/**
 * API Client Configuration
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';

/**
 * Create Axios instance with base configuration
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

/**
 * Request Interceptor
 *
 * Automatically adds JWT token to Authorization header for authenticated requests.
 * Retrieves token from localStorage (auth-storage) to avoid circular dependency with authStore.
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from localStorage (Zustand persist storage)
    const authStorage = localStorage.getItem('auth-storage');

    if (authStorage) {
      try {
        const { state } = JSON.parse(authStorage);
        const token = state?.token;

        if (token) {
          // Add Authorization header with Bearer token
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Failed to parse auth storage:', error);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 *
 * Handles common error scenarios:
 * - 401 Unauthorized: Token expired or invalid, clear auth state
 * - 403 Forbidden: User doesn't have permission
 * - 422 Unprocessable Entity: Validation errors
 * - 500+ Server errors
 * - Network errors: Queues mutation requests for offline sync
 */
apiClient.interceptors.response.use(
  (response) => {
    // Check for JWT token in response headers (from login/register)
    const authHeader = response.headers.authorization || response.headers.Authorization;
    if (authHeader && authHeader.startsWith('Bearer ')) {
      const token = authHeader.replace('Bearer ', '');

      // Update token in localStorage if present
      const authStorage = localStorage.getItem('auth-storage');
      if (authStorage) {
        try {
          const parsed = JSON.parse(authStorage);
          parsed.state.token = token;
          localStorage.setItem('auth-storage', JSON.stringify(parsed));
        } catch (error) {
          console.error('Failed to update token in storage:', error);
        }
      }
    }

    return response;
  },
  async (error: AxiosError) => {
    if (error.response) {
      const status = error.response.status;

      // Handle 401 Unauthorized - clear auth state and redirect to login
      if (status === 401) {
        // Clear auth storage
        localStorage.removeItem('auth-storage');

        // Redirect to login page if not already there
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }

      // Handle 403 Forbidden
      if (status === 403) {
        console.error('Access forbidden:', error.response.data);
      }

      // Handle 422 Unprocessable Entity (validation errors)
      if (status === 422) {
        console.error('Validation error:', error.response.data);
      }

      // Handle 500+ Server errors
      if (status >= 500) {
        console.error('Server error:', error.response.data);
      }
    } else if (error.request) {
      // Request was made but no response received (network error)
      // Queue the request for retry when connection is restored
      if (!navigator.onLine && error.config) {
        // Only queue mutation requests (POST, PUT, PATCH, DELETE)
        const method = error.config.method?.toUpperCase();
        if (method && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
          try {
            await addToQueue(error.config);
          } catch (queueError) {
            console.error('Failed to queue request for offline sync:', queueError);
          }
        }
      }

      console.error('Network error:', error.message);
    } else {
      // Something else happened
      console.error('Error:', error.message);
    }

    return Promise.reject(error);
  }
);

/**
 * API Client Instance
 *
 * Usage:
 * ```typescript
 * import api from './services/api';
 *
 * // GET request
 * const response = await api.get('/recipes');
 *
 * // POST request
 * const response = await api.post('/recipes', { title: 'New Recipe' });
 *
 * // PUT request
 * const response = await api.put('/recipes/1', { title: 'Updated Recipe' });
 *
 * // DELETE request
 * const response = await api.delete('/recipes/1');
 * ```
 */
export default apiClient;

/**
 * Export as named export for explicit imports
 */
export { apiClient as api };
